// Lightweight i18n for Little Ark. Two locales: English + Kannada.
// Usage:
//   <LanguageProvider><App/></LanguageProvider>
//   const { t, lang, setLang } = useT();
//
// Keys are dot-namespaced (e.g. "hero.headline").
// If a key is missing, the raw key is returned in dev — never crashes.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "la_lang";
export const LANGUAGES = [
  { code: "en", label: "EN", full: "English" },
  { code: "kn", label: "ಕನ್ನಡ", full: "Kannada" },
];

// ===== Translation dictionaries =====
const en = {
  // Navigation
  "nav.about": "About",
  "nav.programs": "Programs",
  "nav.curriculum": "Curriculum",
  "nav.daycare": "Day Care",
  "nav.gallery": "Gallery",
  "nav.parents": "Parents",
  "nav.contact": "Contact",
  "nav.bookTour": "Book a Tour",

  // Hero
  "hero.badge": "Admissions Open 2025 – 2026",
  "hero.line1": "Big Dreams",
  "hero.line2": "Begin at",
  "hero.line3": "Little Ark",
  "hero.tagline": "Activity-based learning for happy little minds. A warm, safe & playful home for children aged 2 – 6 in Harohalli, Bengaluru.",
  "hero.ctaTour": "Book a School Tour",
  "hero.ctaAdmissions": "Admissions Open",
  "hero.safeCampus": "CCTV-Safe Campus",
  "hero.caringTeachers": "Caring Teachers",
  "hero.happyKids": "Happy Kids",
  "hero.happyKidsSub": "Every single day",
  "hero.activityBased": "Activity Based",
  "hero.activityBasedSub": "Hands-on learning",

  // Marquee
  "marquee.learn": "Learn",
  "marquee.play": "Play",
  "marquee.explore": "Explore",
  "marquee.grow": "Grow",
  "marquee.imagine": "Imagine",
  "marquee.create": "Create",
  "marquee.dream": "Dream",

  // About
  "about.badge": "About Little Ark",
  "about.headline": "A caring home where little minds",
  "about.headlineAccent": "bloom joyfully.",
  "about.body": "At Little Ark, we believe every child carries a universe inside. Through warm personal attention, joyful play and a thoughtfully designed environment, we help your little one grow into a happy, curious and confident learner.",
  "about.card.learn": "Learn",
  "about.card.learnDesc": "Phonics, numbers & curiosity",
  "about.card.play": "Play",
  "about.card.playDesc": "Sensory, role-play, building",
  "about.card.explore": "Explore",
  "about.card.exploreDesc": "Outdoor adventures & nature",
  "about.card.grow": "Grow",
  "about.card.growDesc": "Confidence, kindness, friendship",

  // Programs
  "programs.badge": "Our Programs",
  "programs.headline": "Programs that grow",
  "programs.headlineAccent": "with your child.",
  "programs.subhead": "From first steps to school-ready confidence — a thoughtfully crafted journey for every age.",
  "programs.playgroup": "Playgroup",
  "programs.nursery": "Nursery",
  "programs.lkg": "LKG",
  "programs.ukg": "UKG",
  "programs.daycare": "Day Care",
  "programs.playgroup.focus": "Sensory play & social bonding",
  "programs.nursery.focus": "Pre-writing, phonics, motor skills",
  "programs.lkg.focus": "Reading readiness & numeracy",
  "programs.ukg.focus": "School-ready confidence & creativity",
  "programs.daycare.focus": "Safe full-day care, meals & nap",
  "programs.enquire": "Enquire about",

  // Curriculum
  "curriculum.badge": "Activity-Based Curriculum",
  "curriculum.headline": "Learning through joyful play.",
  "curriculum.body": "Our curriculum is hands-on, sensory-rich and rooted in how children naturally learn — through doing, exploring, asking and creating.",
  "curriculum.handsOn": "Hands-on Learning",
  "curriculum.storytelling": "Storytelling",
  "curriculum.phonics": "Phonics",
  "curriculum.sensory": "Sensory Play",
  "curriculum.outdoor": "Outdoor Exploration",
  "curriculum.art": "Art & Craft",
  "curriculum.music": "Music & Movement",
  "curriculum.communication": "Communication",

  // Manifesto
  "manifesto.eyebrow": "Our Philosophy",
  "manifesto.headline1": "We don't just prepare children for school.",
  "manifesto.headline2": "We prepare them for life.",
  "manifesto.c1.title": "Every child is a whole world.",
  "manifesto.c1.body": "We slow down and meet each little one exactly where they are — with patience, warmth and wonder — so learning always begins from a place of belonging.",
  "manifesto.c2.title": "Play is serious work.",
  "manifesto.c2.body": "Sandcastles, songs and splashing puddles aren't breaks from learning — they are the learning. Curiosity is the syllabus and joy is the method.",
  "manifesto.c3.title": "Confidence is taught gently.",
  "manifesto.c3.body": "Small classes, caring teachers and safe spaces let children take brave little risks — raising a hand, making a friend, trying again.",
  "manifesto.c4.title": "Big dreams start small.",
  "manifesto.c4.body": "Today a curious child; tomorrow a doctor, an artist, a leader. We plant the seeds of who they'll become — one happy day at a time.",

  // Day Care
  "daycare.badge": "Day Care",
  "daycare.headline": "A second home when you're at work.",
  "daycare.body": "Parents trust Little Ark for full-day care that feels like home — warm, safe, joyful and full of structured, nurturing activities.",
  "daycare.feature.safe": "Safe & secure environment",
  "daycare.feature.staff": "Caring, trained staff",
  "daycare.feature.cctv": "CCTV monitored campus",
  "daycare.feature.indoor": "Engaging indoor activities",
  "daycare.feature.meals": "Healthy meals & routine",
  "daycare.feature.nap": "Cozy nap area",
  "daycare.cta": "Ask About Day Care",
  "daycare.safeBadge": "100% Safe",
  "daycare.safeBadgeSub": "CCTV + trained staff",

  // Why Choose
  "why.badge": "Why Parents Choose Us",
  "why.headline": "Trusted by parents across",
  "why.headlineAccent": "Harohalli.",
  "why.stat.happyKids": "Happy Kids",
  "why.stat.teachers": "Certified Teachers",
  "why.stat.years": "Years of Care",
  "why.stat.safe": "Safe & Secure",
  "why.card.small": "Small class sizes",
  "why.card.smallDesc": "Personal attention to each child",
  "why.card.caring": "Caring teachers",
  "why.card.caringDesc": "Warm, trained, child-first mentors",
  "why.card.safe": "Safe environment",
  "why.card.safeDesc": "CCTV + secure, hygienic spaces",
  "why.card.creative": "Creative learning",
  "why.card.creativeDesc": "Art, music, storytelling & play",
  "why.card.outdoor": "Outdoor play",
  "why.card.outdoorDesc": "Nature, sand, water & adventure",
  "why.card.holistic": "Holistic growth",
  "why.card.holisticDesc": "Mind, body and heart together",

  // Playground
  "playground.badge": "Playground & Splash Area",
  "playground.headline": "Sunshine, splashes & sandcastles.",
  "playground.body": "Outdoor play is where kids grow brave, build friendships and burn energy joyfully.",
  "playground.openAir": "Open-Air Playground",
  "playground.openAirSub": "Slides, swings, ride-ons & more",
  "playground.splash": "Splash Zone",
  "playground.splashSub": "Cool down with safe water play",
  "playground.sandbox": "Sandbox",
  "playground.splashWater": "Splash Water Play",
  "playground.slides": "Slides & Swings",
  "playground.activity": "Activity Area",

  // Gallery
  "gallery.badge": "Gallery",
  "gallery.headline": "A peek into our world.",
  "gallery.body": "Moments of curiosity, friendship & joy — captured every day at Little Ark.",
  "gallery.classroom": "Bright classrooms",
  "gallery.art": "Art & craft",
  "gallery.outdoor": "Outdoor play",
  "gallery.smiles": "Joyful smiles",
  "gallery.splash": "Splash play",
  "gallery.dreams": "Future dreams",

  // Future Dreams
  "future.badge": "Future Dreams",
  "future.headline1": "Today a Child.",
  "future.headline2": "Tomorrow a Leader.",
  "future.body": "We help every child imagine, believe and build their tomorrow. From doctors to astronauts to entrepreneurs — big dreams begin small.",
  "future.doctor": "Doctor",
  "future.astronaut": "Astronaut",
  "future.scientist": "Scientist",
  "future.police": "Police Officer",
  "future.athlete": "Athlete",
  "future.entrepreneur": "Entrepreneur",

  // Testimonials
  "testimonials.badge": "Parents Love Us",
  "testimonials.headline": "Stories from happy",
  "testimonials.headlineAccent": "Little Ark families.",
  "testimonials.t1.quote": "My son went from shy to confident in just one term. The teachers at Little Ark truly care — I get a warm update every single day.",
  "testimonials.t1.role": "Parent of Aarav (Nursery)",
  "testimonials.t2.quote": "Clean campus, CCTV everywhere, and most importantly — warm staff. We feel completely at peace leaving our daughter here.",
  "testimonials.t2.role": "Parent of Diya (LKG)",
  "testimonials.t3.quote": "The activity-based learning is wonderful. My toddler comes home singing songs, naming colors and asking the cutest questions!",
  "testimonials.t3.role": "Parent of Vihaan (Playgroup)",
  "testimonials.t4.quote": "As a working parent, day care at Little Ark has been a blessing. Healthy meals, nap time, learning — all under one safe roof.",
  "testimonials.t4.role": "Parent of Kavya (Day Care)",

  // Admission (contact section on home)
  "admission.badge": "Admissions Open",
  "admission.headline": "Give your child a",
  "admission.headlineAccent": "joyful beginning.",
  "admission.body": "Book a school tour or get answers — we'd love to hear from you.",
  "admission.visitUs": "Visit Us",
  "admission.schoolHours": "School Hours",
  "admission.callNow": "Call Now",
  "admission.whatsapp": "WhatsApp",
  "admission.whatsappSub": "Quick reply",

  // Inquiry Form
  "inquiry.parentName": "Parent's Name",
  "inquiry.parentNamePh": "e.g. Priya Sharma",
  "inquiry.phone": "Phone / WhatsApp",
  "inquiry.phonePh": "+91 98xxx xxxxx",
  "inquiry.email": "Email",
  "inquiry.emailPh": "you@email.com",
  "inquiry.program": "Interested Program",
  "inquiry.programPh": "Choose program",
  "inquiry.childName": "Child's Name",
  "inquiry.childNamePh": "Little one's name",
  "inquiry.childAge": "Child's Age",
  "inquiry.childAgePh": "e.g. 3 years",
  "inquiry.message": "Anything else you'd like to share?",
  "inquiry.messagePh": "Tell us about your child or your questions…",
  "inquiry.send": "Send Inquiry",
  "inquiry.sending": "Sending…",
  "inquiry.disclaimer": "After submitting, WhatsApp opens with your details pre-filled — just tap send to confirm and get a quick reply.",
  "inquiry.successTitle": "Thank you for reaching out!",
  "inquiry.successBody": "We've received your inquiry. Tap the button below to confirm on WhatsApp — you'll get a quick reply and our team will call you within one working day to schedule your school tour.",
  "inquiry.confirmWa": "Confirm on WhatsApp",
  "inquiry.submitAnother": "Submit another inquiry",
  "inquiry.errRequired": "Please share your name and phone so we can reach you.",
  "inquiry.errRate": "Too many requests. Please try again later or message us on WhatsApp.",
  "inquiry.errGeneric": "We couldn't submit your inquiry. Please try WhatsApp.",
  "inquiry.thankToast": "Thank you! Opening WhatsApp to confirm your inquiry…",
  "inquiry.fallbackToast": "Tap 'Confirm on WhatsApp' to finish your inquiry.",

  // Footer
  "footer.tagline": "A safe, joyful and activity-based learning home for little dreamers aged 2 – 6. Big dreams begin here.",
  "footer.visitUs": "Visit Us",
  "footer.admissions": "Admissions",
  "footer.rights": "All rights reserved.",
  "footer.adminLogin": "Admin Login",
  "footer.madeWith": "Made with",
  "footer.forDreamers": "for little dreamers",

  // WhatsApp Float
  "wa.float": "Chat with us",

  // Language
  "lang.label": "Language",
  "lang.en": "English",
  "lang.kn": "ಕನ್ನಡ",

  // Admission Form (public)
  "af.title": "Student Admission Form",
  "af.subtitle": "Play · Learn · Grow",
  "af.subtitle2": "NEP 2020 inspired curriculum",
  "af.step.student": "Student",
  "af.step.address": "Address",
  "af.step.parents": "Parents",
  "af.step.medical": "Medical & School",
  "af.step.pickup": "Pickup & Docs",
  "af.section.seeking": "1. Admission Seeking For",
  "af.section.student": "2. Student Information",
  "af.section.address": "3. Address",
  "af.section.father": "4. Father Details",
  "af.section.mother": "5. Mother Details",
  "af.section.medical": "6. Medical Information",
  "af.section.prevSchool": "7. Previous School",
  "af.section.pickup": "8. Pick-up & Drop",
  "af.section.emergency": "9. Emergency Contact",
  "af.section.docs": "10. Documents Submitted",
  "af.section.declaration": "11. Parent Declaration",
  "af.field.firstName": "First Name",
  "af.field.middleName": "Middle Name",
  "af.field.lastName": "Last Name",
  "af.field.dob": "Date of Birth",
  "af.field.gender": "Gender",
  "af.field.male": "Male",
  "af.field.female": "Female",
  "af.field.bloodGroup": "Blood Group",
  "af.field.bloodGroupPh": "e.g. O+",
  "af.field.aadhaar": "Aadhaar Number (Optional)",
  "af.field.nationality": "Nationality",
  "af.field.motherTongue": "Mother Tongue",
  "af.field.religion": "Religion",
  "af.field.otherLangs": "Other Languages",
  "af.field.category": "Category (Optional)",
  "af.field.photo": "Student Passport Photo (Optional, 35×45mm)",
  "af.field.house": "House No.",
  "af.field.street": "Street",
  "af.field.area": "Area",
  "af.field.landmark": "Landmark",
  "af.field.city": "Village / City",
  "af.field.taluk": "Taluk",
  "af.field.district": "District",
  "af.field.state": "State",
  "af.field.pin": "PIN Code",
  "af.field.fullName": "Full Name",
  "af.field.qualification": "Qualification",
  "af.field.occupation": "Occupation",
  "af.field.company": "Company Name",
  "af.field.mobile": "Mobile Number",
  "af.field.whatsapp": "WhatsApp Number",
  "af.field.emailId": "Email ID",
  "af.field.specialNeeds": "Special Needs",
  "af.field.allergies": "Allergies",
  "af.field.medConditions": "Medical Conditions",
  "af.field.medication": "Current Medication",
  "af.field.doctor": "Doctor Name",
  "af.field.hospital": "Hospital",
  "af.field.emergencyNumber": "Emergency Contact Number",
  "af.field.prevSchool": "School Name",
  "af.field.prevClass": "Class Studied",
  "af.field.prevReason": "Reason for Leaving",
  "af.field.pickupOther": "Specify other pickup type",
  "af.field.authorizedName": "Authorized Person Name",
  "af.field.relationship": "Relationship",
  "af.field.phoneNumber": "Phone Number",
  "af.field.addressIfDiff": "Address (if different)",
  "af.field.name": "Name",
  "af.field.altPhone": "Alternate Phone",
  "af.pickup.parent": "Parent",
  "af.pickup.grandparent": "Grandparent",
  "af.pickup.driver": "Driver",
  "af.pickup.van": "Van Service",
  "af.pickup.other": "Other",
  "af.doc.birth": "Birth Certificate",
  "af.doc.aadhaar": "Aadhaar Copy",
  "af.doc.photos": "Passport Photos",
  "af.doc.vaccine": "Vaccination Record",
  "af.doc.address": "Address Proof",
  "af.doc.parentId": "Parent ID Proof",
  "af.doc.tc": "Transfer Certificate (if applicable)",
  "af.declarationText": "I hereby declare that the information provided is true and correct. I agree to abide by the rules and regulations of Little Ark Preschool. I understand a physical signature will be collected at the time of admission.",
  "af.back": "Back",
  "af.continue": "Continue",
  "af.submit": "Submit Admission Form",
  "af.submitting": "Submitting…",
  "af.errRequired": "Please fill required fields.",
  "af.errDeclaration": "Please agree to the parent declaration.",
  "af.errGeneric": "Could not submit. Please try again.",
  "af.invalid.title": "Link unavailable",
  "af.invalid.help": "Please contact the coordinator at Little Ark to request a fresh admission link.",
  "af.invalid.home": "Visit Little Ark",
  "af.done.title": "Admission form submitted!",
  "af.done.body": "Thank you for choosing Little Ark Preschool for {name}. Our coordinator will get in touch with you shortly to complete the process.",
  "af.done.home": "Visit Home",
};

const kn = {
  // Navigation (ಸಂಚಾರ)
  "nav.about": "ಪರಿಚಯ",
  "nav.programs": "ಕಾರ್ಯಕ್ರಮಗಳು",
  "nav.curriculum": "ಪಠ್ಯಕ್ರಮ",
  "nav.daycare": "ಡೇ ಕೇರ್",
  "nav.gallery": "ಗ್ಯಾಲರಿ",
  "nav.parents": "ಪೋಷಕರು",
  "nav.contact": "ಸಂಪರ್ಕ",
  "nav.bookTour": "ಶಾಲಾ ಭೇಟಿ ಬುಕ್ ಮಾಡಿ",

  // Hero
  "hero.badge": "ಪ್ರವೇಶ ಆರಂಭ 2025 – 2026",
  "hero.line1": "ದೊಡ್ಡ ಕನಸುಗಳು",
  "hero.line2": "ಆರಂಭವಾಗುತ್ತವೆ",
  "hero.line3": "ಲಿಟಲ್ ಆರ್ಕ್‌ನಲ್ಲಿ",
  "hero.tagline": "ಸಂತೋಷದ ಪುಟ್ಟ ಮನಸ್ಸುಗಳಿಗಾಗಿ ಚಟುವಟಿಕೆ-ಆಧಾರಿತ ಕಲಿಕೆ. ಹರೋಹಳ್ಳಿ, ಬೆಂಗಳೂರಿನಲ್ಲಿ 2 – 6 ವಯಸ್ಸಿನ ಮಕ್ಕಳಿಗೆ ಬೆಚ್ಚಗಿನ, ಸುರಕ್ಷಿತ ಮತ್ತು ಆಟದ ಮನೆ.",
  "hero.ctaTour": "ಶಾಲಾ ಭೇಟಿ ಬುಕ್ ಮಾಡಿ",
  "hero.ctaAdmissions": "ಪ್ರವೇಶ ಆರಂಭ",
  "hero.safeCampus": "ಸಿಸಿಟಿವಿ ಸುರಕ್ಷಿತ ಕ್ಯಾಂಪಸ್",
  "hero.caringTeachers": "ಪ್ರೀತಿಯ ಶಿಕ್ಷಕರು",
  "hero.happyKids": "ಸಂತೋಷದ ಮಕ್ಕಳು",
  "hero.happyKidsSub": "ಪ್ರತಿ ದಿನವೂ",
  "hero.activityBased": "ಚಟುವಟಿಕೆ ಆಧಾರಿತ",
  "hero.activityBasedSub": "ಅನುಭವಪೂರ್ವಕ ಕಲಿಕೆ",

  // Marquee
  "marquee.learn": "ಕಲಿಯಿರಿ",
  "marquee.play": "ಆಡಿ",
  "marquee.explore": "ಅನ್ವೇಷಿಸಿ",
  "marquee.grow": "ಬೆಳೆಯಿರಿ",
  "marquee.imagine": "ಕಲ್ಪಿಸಿ",
  "marquee.create": "ರಚಿಸಿ",
  "marquee.dream": "ಕನಸು ಕಾಣಿ",

  // About
  "about.badge": "ಲಿಟಲ್ ಆರ್ಕ್ ಬಗ್ಗೆ",
  "about.headline": "ಪುಟ್ಟ ಮನಸ್ಸುಗಳು",
  "about.headlineAccent": "ಸಂತೋಷದಿಂದ ಅರಳುವ ಪ್ರೀತಿಯ ಮನೆ.",
  "about.body": "ಲಿಟಲ್ ಆರ್ಕ್‌ನಲ್ಲಿ, ಪ್ರತಿ ಮಗುವಿನೊಳಗೆ ಒಂದು ಬ್ರಹ್ಮಾಂಡವಿದೆ ಎಂದು ನಾವು ನಂಬುತ್ತೇವೆ. ಬೆಚ್ಚಗಿನ ವೈಯಕ್ತಿಕ ಗಮನ, ಸಂತೋಷದ ಆಟ ಮತ್ತು ಚಿಂತನಶೀಲವಾಗಿ ವಿನ್ಯಾಸಗೊಳಿಸಿದ ಪರಿಸರದ ಮೂಲಕ, ನಾವು ನಿಮ್ಮ ಪುಟ್ಟ ಮಗುವನ್ನು ಸಂತೋಷದ, ಕುತೂಹಲಿ ಮತ್ತು ಆತ್ಮವಿಶ್ವಾಸಿ ಕಲಿಯುವವನನ್ನಾಗಿ ಬೆಳೆಸಲು ಸಹಾಯ ಮಾಡುತ್ತೇವೆ.",
  "about.card.learn": "ಕಲಿಯಿರಿ",
  "about.card.learnDesc": "ಫೋನಿಕ್ಸ್, ಸಂಖ್ಯೆಗಳು ಮತ್ತು ಕುತೂಹಲ",
  "about.card.play": "ಆಡಿ",
  "about.card.playDesc": "ಇಂದ್ರಿಯ, ಪಾತ್ರಾಭಿನಯ, ಕಟ್ಟುವಿಕೆ",
  "about.card.explore": "ಅನ್ವೇಷಿಸಿ",
  "about.card.exploreDesc": "ಹೊರಾಂಗಣ ಸಾಹಸ ಮತ್ತು ಪ್ರಕೃತಿ",
  "about.card.grow": "ಬೆಳೆಯಿರಿ",
  "about.card.growDesc": "ಆತ್ಮವಿಶ್ವಾಸ, ದಯೆ, ಗೆಳೆತನ",

  // Programs
  "programs.badge": "ನಮ್ಮ ಕಾರ್ಯಕ್ರಮಗಳು",
  "programs.headline": "ನಿಮ್ಮ ಮಗುವಿನೊಂದಿಗೆ",
  "programs.headlineAccent": "ಬೆಳೆಯುವ ಕಾರ್ಯಕ್ರಮಗಳು.",
  "programs.subhead": "ಮೊದಲ ಹೆಜ್ಜೆಯಿಂದ ಶಾಲೆಗೆ ಸಿದ್ಧವಾದ ಆತ್ಮವಿಶ್ವಾಸದವರೆಗೆ — ಪ್ರತಿ ವಯಸ್ಸಿಗಾಗಿ ಚಿಂತನಶೀಲವಾಗಿ ರೂಪಿಸಿದ ಪ್ರಯಾಣ.",
  "programs.playgroup": "ಪ್ಲೇಗ್ರೂಪ್",
  "programs.nursery": "ನರ್ಸರಿ",
  "programs.lkg": "ಎಲ್‌ಕೆಜಿ",
  "programs.ukg": "ಯುಕೆಜಿ",
  "programs.daycare": "ಡೇ ಕೇರ್",
  "programs.playgroup.focus": "ಇಂದ್ರಿಯ ಆಟ ಮತ್ತು ಸಾಮಾಜಿಕ ಬಂಧನ",
  "programs.nursery.focus": "ಮೊದಲು-ಬರೆಯುವಿಕೆ, ಫೋನಿಕ್ಸ್, ಚಲನಾ ಕೌಶಲ್ಯ",
  "programs.lkg.focus": "ಓದುವ ಸಿದ್ಧತೆ ಮತ್ತು ಸಂಖ್ಯಾ ಜ್ಞಾನ",
  "programs.ukg.focus": "ಶಾಲಾ-ಸಿದ್ಧ ಆತ್ಮವಿಶ್ವಾಸ ಮತ್ತು ಸೃಜನಶೀಲತೆ",
  "programs.daycare.focus": "ಸುರಕ್ಷಿತ ಪೂರ್ಣ-ದಿನದ ಆರೈಕೆ, ಊಟ ಮತ್ತು ನಿದ್ರೆ",
  "programs.enquire": "ವಿಚಾರಿಸಿ",

  // Curriculum
  "curriculum.badge": "ಚಟುವಟಿಕೆ-ಆಧಾರಿತ ಪಠ್ಯಕ್ರಮ",
  "curriculum.headline": "ಸಂತೋಷದ ಆಟದ ಮೂಲಕ ಕಲಿಕೆ.",
  "curriculum.body": "ನಮ್ಮ ಪಠ್ಯಕ್ರಮವು ಅನುಭವಪೂರ್ವಕ, ಇಂದ್ರಿಯ-ಸಮೃದ್ಧವಾಗಿದೆ ಮತ್ತು ಮಕ್ಕಳು ಸಹಜವಾಗಿ ಕಲಿಯುವ ರೀತಿಯನ್ನು ಆಧರಿಸಿದೆ — ಮಾಡುವ, ಅನ್ವೇಷಿಸುವ, ಕೇಳುವ ಮತ್ತು ರಚಿಸುವ ಮೂಲಕ.",
  "curriculum.handsOn": "ಅನುಭವಪೂರ್ವಕ ಕಲಿಕೆ",
  "curriculum.storytelling": "ಕಥೆ ಹೇಳುವಿಕೆ",
  "curriculum.phonics": "ಫೋನಿಕ್ಸ್",
  "curriculum.sensory": "ಇಂದ್ರಿಯ ಆಟ",
  "curriculum.outdoor": "ಹೊರಾಂಗಣ ಅನ್ವೇಷಣೆ",
  "curriculum.art": "ಕಲೆ ಮತ್ತು ಕರಕುಶಲ",
  "curriculum.music": "ಸಂಗೀತ ಮತ್ತು ಚಲನೆ",
  "curriculum.communication": "ಸಂವಹನ",

  // Manifesto
  "manifesto.eyebrow": "ನಮ್ಮ ತತ್ವಶಾಸ್ತ್ರ",
  "manifesto.headline1": "ನಾವು ಮಕ್ಕಳನ್ನು ಕೇವಲ ಶಾಲೆಗಾಗಿ ಸಿದ್ಧಪಡಿಸುವುದಿಲ್ಲ.",
  "manifesto.headline2": "ನಾವು ಜೀವನಕ್ಕಾಗಿ ಸಿದ್ಧಪಡಿಸುತ್ತೇವೆ.",
  "manifesto.c1.title": "ಪ್ರತಿ ಮಗುವೂ ಒಂದು ಪೂರ್ಣ ಪ್ರಪಂಚ.",
  "manifesto.c1.body": "ನಾವು ನಿಧಾನವಾಗಿ ಚಲಿಸಿ ಪ್ರತಿ ಪುಟ್ಟ ಮಗುವನ್ನು ಅವರಿರುವ ಸ್ಥಳದಲ್ಲಿಯೇ ಭೇಟಿಯಾಗುತ್ತೇವೆ — ತಾಳ್ಮೆ, ಪ್ರೀತಿ ಮತ್ತು ಅಚ್ಚರಿಯೊಂದಿಗೆ — ಆದ್ದರಿಂದ ಕಲಿಕೆ ಯಾವಾಗಲೂ ಸೇರಿಕೊಳ್ಳುವ ಭಾವನೆಯಿಂದ ಆರಂಭವಾಗುತ್ತದೆ.",
  "manifesto.c2.title": "ಆಟವು ಗಂಭೀರ ಕೆಲಸ.",
  "manifesto.c2.body": "ಮರಳಿನ ಕೋಟೆಗಳು, ಹಾಡುಗಳು ಮತ್ತು ನೀರಿನಲ್ಲಿ ಸ್ಪ್ಲಾಶ್ ಮಾಡುವುದು ಕಲಿಕೆಯಿಂದ ವಿರಾಮಗಳಲ್ಲ — ಅವೇ ಕಲಿಕೆ. ಕುತೂಹಲವು ಪಠ್ಯಕ್ರಮ ಮತ್ತು ಸಂತೋಷವು ವಿಧಾನ.",
  "manifesto.c3.title": "ಆತ್ಮವಿಶ್ವಾಸವನ್ನು ಮೃದುವಾಗಿ ಕಲಿಸಲಾಗುತ್ತದೆ.",
  "manifesto.c3.body": "ಸಣ್ಣ ತರಗತಿಗಳು, ಪ್ರೀತಿಯ ಶಿಕ್ಷಕರು ಮತ್ತು ಸುರಕ್ಷಿತ ಸ್ಥಳಗಳು ಮಕ್ಕಳಿಗೆ ಪುಟ್ಟ ಧೈರ್ಯದ ಹೆಜ್ಜೆಗಳನ್ನು ತೆಗೆದುಕೊಳ್ಳಲು ಅವಕಾಶ ನೀಡುತ್ತವೆ — ಕೈ ಎತ್ತುವುದು, ಸ್ನೇಹಿತನನ್ನು ಮಾಡುವುದು, ಮತ್ತೆ ಪ್ರಯತ್ನಿಸುವುದು.",
  "manifesto.c4.title": "ದೊಡ್ಡ ಕನಸುಗಳು ಚಿಕ್ಕದಾಗಿ ಆರಂಭವಾಗುತ್ತವೆ.",
  "manifesto.c4.body": "ಇಂದು ಒಬ್ಬ ಕುತೂಹಲಿ ಮಗು; ನಾಳೆ ಒಬ್ಬ ವೈದ್ಯ, ಕಲಾವಿದ, ನಾಯಕ. ಅವರು ಆಗುವ ವ್ಯಕ್ತಿಯ ಬೀಜಗಳನ್ನು ನಾವು ಬಿತ್ತುತ್ತೇವೆ — ಪ್ರತಿ ಸಂತೋಷದ ದಿನ ಒಂದೊಂದಾಗಿ.",

  // Day Care
  "daycare.badge": "ಡೇ ಕೇರ್",
  "daycare.headline": "ನೀವು ಕೆಲಸಕ್ಕಿರುವಾಗ ಎರಡನೆಯ ಮನೆ.",
  "daycare.body": "ಪೋಷಕರು ಲಿಟಲ್ ಆರ್ಕ್‌ನ ಪೂರ್ಣ-ದಿನದ ಆರೈಕೆಯನ್ನು ನಂಬುತ್ತಾರೆ — ಬೆಚ್ಚಗಿನ, ಸುರಕ್ಷಿತ, ಸಂತೋಷದ ಮತ್ತು ಸಂಘಟಿತ, ಪೋಷಣಾತ್ಮಕ ಚಟುವಟಿಕೆಗಳಿಂದ ತುಂಬಿದೆ.",
  "daycare.feature.safe": "ಸುರಕ್ಷಿತ ಪರಿಸರ",
  "daycare.feature.staff": "ಪ್ರೀತಿಯ, ತರಬೇತಿ ಪಡೆದ ಸಿಬ್ಬಂದಿ",
  "daycare.feature.cctv": "ಸಿಸಿಟಿವಿ ಮೇಲ್ವಿಚಾರಣೆಯ ಕ್ಯಾಂಪಸ್",
  "daycare.feature.indoor": "ಆಸಕ್ತಿದಾಯಕ ಒಳಾಂಗಣ ಚಟುವಟಿಕೆಗಳು",
  "daycare.feature.meals": "ಆರೋಗ್ಯಕರ ಊಟ ಮತ್ತು ದಿನಚರಿ",
  "daycare.feature.nap": "ಆರಾಮದಾಯಕ ನಿದ್ರೆಯ ಪ್ರದೇಶ",
  "daycare.cta": "ಡೇ ಕೇರ್ ಬಗ್ಗೆ ಕೇಳಿ",
  "daycare.safeBadge": "100% ಸುರಕ್ಷಿತ",
  "daycare.safeBadgeSub": "ಸಿಸಿಟಿವಿ + ತರಬೇತಿ ಪಡೆದ ಸಿಬ್ಬಂದಿ",

  // Why Choose
  "why.badge": "ಪೋಷಕರು ನಮ್ಮನ್ನು ಏಕೆ ಆಯ್ಕೆ ಮಾಡುತ್ತಾರೆ",
  "why.headline": "ಪೋಷಕರಿಂದ ವಿಶ್ವಾಸ",
  "why.headlineAccent": "ಹರೋಹಳ್ಳಿಯಾದ್ಯಂತ.",
  "why.stat.happyKids": "ಸಂತೋಷದ ಮಕ್ಕಳು",
  "why.stat.teachers": "ಪ್ರಮಾಣೀಕೃತ ಶಿಕ್ಷಕರು",
  "why.stat.years": "ವರ್ಷಗಳ ಆರೈಕೆ",
  "why.stat.safe": "ಸುರಕ್ಷಿತ",
  "why.card.small": "ಸಣ್ಣ ತರಗತಿಗಳು",
  "why.card.smallDesc": "ಪ್ರತಿ ಮಗುವಿಗೆ ವೈಯಕ್ತಿಕ ಗಮನ",
  "why.card.caring": "ಪ್ರೀತಿಯ ಶಿಕ್ಷಕರು",
  "why.card.caringDesc": "ಬೆಚ್ಚಗಿನ, ತರಬೇತಿ ಪಡೆದ, ಮಕ್ಕಳ-ಆದ್ಯತೆ ಮಾರ್ಗದರ್ಶಕರು",
  "why.card.safe": "ಸುರಕ್ಷಿತ ಪರಿಸರ",
  "why.card.safeDesc": "ಸಿಸಿಟಿವಿ + ಸುರಕ್ಷಿತ, ಸ್ವಚ್ಛ ಸ್ಥಳಗಳು",
  "why.card.creative": "ಸೃಜನಶೀಲ ಕಲಿಕೆ",
  "why.card.creativeDesc": "ಕಲೆ, ಸಂಗೀತ, ಕಥೆ ಹೇಳುವಿಕೆ ಮತ್ತು ಆಟ",
  "why.card.outdoor": "ಹೊರಾಂಗಣ ಆಟ",
  "why.card.outdoorDesc": "ಪ್ರಕೃತಿ, ಮರಳು, ನೀರು ಮತ್ತು ಸಾಹಸ",
  "why.card.holistic": "ಸಮಗ್ರ ಬೆಳವಣಿಗೆ",
  "why.card.holisticDesc": "ಮನಸ್ಸು, ದೇಹ ಮತ್ತು ಹೃದಯ ಒಟ್ಟಿಗೆ",

  // Playground
  "playground.badge": "ಆಟದ ಮೈದಾನ ಮತ್ತು ಸ್ಪ್ಲಾಶ್ ಪ್ರದೇಶ",
  "playground.headline": "ಸೂರ್ಯಪ್ರಕಾಶ, ಸ್ಪ್ಲಾಶ್‌ಗಳು ಮತ್ತು ಮರಳಿನ ಕೋಟೆಗಳು.",
  "playground.body": "ಹೊರಾಂಗಣ ಆಟದಲ್ಲಿ ಮಕ್ಕಳು ಧೈರ್ಯ ಬೆಳೆಸುತ್ತಾರೆ, ಗೆಳೆತನ ಕಟ್ಟುತ್ತಾರೆ ಮತ್ತು ಸಂತೋಷದಿಂದ ಶಕ್ತಿ ಬಳಸುತ್ತಾರೆ.",
  "playground.openAir": "ಮುಕ್ತ ಆಟದ ಮೈದಾನ",
  "playground.openAirSub": "ಜಾರುಬಂಡಿ, ಜೋಕಾಲಿ, ರೈಡ್-ಆನ್‌ಗಳು ಮತ್ತು ಹೆಚ್ಚು",
  "playground.splash": "ಸ್ಪ್ಲಾಶ್ ವಲಯ",
  "playground.splashSub": "ಸುರಕ್ಷಿತ ನೀರಿನ ಆಟದೊಂದಿಗೆ ತಣ್ಣಗಾಗಿ",
  "playground.sandbox": "ಮರಳು ಪೆಟ್ಟಿಗೆ",
  "playground.splashWater": "ಸ್ಪ್ಲಾಶ್ ನೀರಿನ ಆಟ",
  "playground.slides": "ಜಾರುಬಂಡಿಗಳು ಮತ್ತು ಜೋಕಾಲಿಗಳು",
  "playground.activity": "ಚಟುವಟಿಕೆ ಪ್ರದೇಶ",

  // Gallery
  "gallery.badge": "ಗ್ಯಾಲರಿ",
  "gallery.headline": "ನಮ್ಮ ಪ್ರಪಂಚಕ್ಕೆ ಒಂದು ಇಣುಕುನೋಟ.",
  "gallery.body": "ಕುತೂಹಲ, ಗೆಳೆತನ ಮತ್ತು ಸಂತೋಷದ ಕ್ಷಣಗಳು — ಪ್ರತಿ ದಿನ ಲಿಟಲ್ ಆರ್ಕ್‌ನಲ್ಲಿ ಸೆರೆಹಿಡಿಯಲಾಗುತ್ತದೆ.",
  "gallery.classroom": "ಪ್ರಕಾಶಮಾನ ತರಗತಿಗಳು",
  "gallery.art": "ಕಲೆ ಮತ್ತು ಕರಕುಶಲ",
  "gallery.outdoor": "ಹೊರಾಂಗಣ ಆಟ",
  "gallery.smiles": "ಸಂತೋಷದ ನಗು",
  "gallery.splash": "ಸ್ಪ್ಲಾಶ್ ಆಟ",
  "gallery.dreams": "ಭವಿಷ್ಯದ ಕನಸುಗಳು",

  // Future Dreams
  "future.badge": "ಭವಿಷ್ಯದ ಕನಸುಗಳು",
  "future.headline1": "ಇಂದು ಒಂದು ಮಗು.",
  "future.headline2": "ನಾಳೆ ಒಬ್ಬ ನಾಯಕ.",
  "future.body": "ಪ್ರತಿ ಮಗುವಿಗೆ ಕಲ್ಪಿಸಲು, ನಂಬಲು ಮತ್ತು ಅವರ ನಾಳೆಯನ್ನು ನಿರ್ಮಿಸಲು ಸಹಾಯ ಮಾಡುತ್ತೇವೆ. ವೈದ್ಯರಿಂದ ಗಗನಯಾತ್ರಿಗಳವರೆಗೆ, ಉದ್ಯಮಿಗಳವರೆಗೆ — ದೊಡ್ಡ ಕನಸುಗಳು ಚಿಕ್ಕದಾಗಿ ಆರಂಭವಾಗುತ್ತವೆ.",
  "future.doctor": "ವೈದ್ಯ",
  "future.astronaut": "ಗಗನಯಾತ್ರಿ",
  "future.scientist": "ವಿಜ್ಞಾನಿ",
  "future.police": "ಪೊಲೀಸ್ ಅಧಿಕಾರಿ",
  "future.athlete": "ಕ್ರೀಡಾಪಟು",
  "future.entrepreneur": "ಉದ್ಯಮಿ",

  // Testimonials
  "testimonials.badge": "ಪೋಷಕರು ನಮ್ಮನ್ನು ಪ್ರೀತಿಸುತ್ತಾರೆ",
  "testimonials.headline": "ಸಂತೋಷದ",
  "testimonials.headlineAccent": "ಲಿಟಲ್ ಆರ್ಕ್ ಕುಟುಂಬಗಳ ಕಥೆಗಳು.",
  "testimonials.t1.quote": "ನನ್ನ ಮಗ ಒಂದೇ ಟರ್ಮ್‌ನಲ್ಲಿ ನಾಚಿಕೆಯಿಂದ ಆತ್ಮವಿಶ್ವಾಸಕ್ಕೆ ಬದಲಾದನು. ಲಿಟಲ್ ಆರ್ಕ್‌ನ ಶಿಕ್ಷಕರು ನಿಜವಾಗಿಯೂ ಕಾಳಜಿ ವಹಿಸುತ್ತಾರೆ — ನಾನು ಪ್ರತಿದಿನ ಬೆಚ್ಚಗಿನ ಅಪ್‌ಡೇಟ್ ಪಡೆಯುತ್ತೇನೆ.",
  "testimonials.t1.role": "ಆರವ್ ಅವರ ಪೋಷಕರು (ನರ್ಸರಿ)",
  "testimonials.t2.quote": "ಸ್ವಚ್ಛ ಕ್ಯಾಂಪಸ್, ಎಲ್ಲೆಡೆ ಸಿಸಿಟಿವಿ, ಮತ್ತು ಮುಖ್ಯವಾಗಿ — ಬೆಚ್ಚಗಿನ ಸಿಬ್ಬಂದಿ. ನಮ್ಮ ಮಗಳನ್ನು ಇಲ್ಲಿ ಬಿಟ್ಟುಹೋಗುವಾಗ ನಾವು ಸಂಪೂರ್ಣ ಶಾಂತಿಯಲ್ಲಿದ್ದೇವೆ.",
  "testimonials.t2.role": "ದಿಯಾ ಅವರ ಪೋಷಕರು (ಎಲ್‌ಕೆಜಿ)",
  "testimonials.t3.quote": "ಚಟುವಟಿಕೆ-ಆಧಾರಿತ ಕಲಿಕೆ ಅದ್ಭುತವಾಗಿದೆ. ನನ್ನ ಮಗು ಮನೆಗೆ ಬಂದು ಹಾಡುಗಳನ್ನು ಹಾಡುತ್ತದೆ, ಬಣ್ಣಗಳನ್ನು ಹೆಸರಿಸುತ್ತದೆ ಮತ್ತು ಮುದ್ದಾದ ಪ್ರಶ್ನೆಗಳನ್ನು ಕೇಳುತ್ತದೆ!",
  "testimonials.t3.role": "ವಿಹಾನ್ ಅವರ ಪೋಷಕರು (ಪ್ಲೇಗ್ರೂಪ್)",
  "testimonials.t4.quote": "ಒಬ್ಬ ಕೆಲಸ ಮಾಡುವ ಪೋಷಕರಾಗಿ, ಲಿಟಲ್ ಆರ್ಕ್‌ನ ಡೇ ಕೇರ್ ಒಂದು ಆಶೀರ್ವಾದವಾಗಿದೆ. ಆರೋಗ್ಯಕರ ಊಟ, ನಿದ್ರೆಯ ಸಮಯ, ಕಲಿಕೆ — ಎಲ್ಲವೂ ಒಂದೇ ಸುರಕ್ಷಿತ ಸೂರಿನ ಕೆಳಗೆ.",
  "testimonials.t4.role": "ಕಾವ್ಯಾ ಅವರ ಪೋಷಕರು (ಡೇ ಕೇರ್)",

  // Admission (contact section on home)
  "admission.badge": "ಪ್ರವೇಶ ಆರಂಭ",
  "admission.headline": "ನಿಮ್ಮ ಮಗುವಿಗೆ ಒಂದು",
  "admission.headlineAccent": "ಸಂತೋಷದ ಆರಂಭ ನೀಡಿ.",
  "admission.body": "ಶಾಲಾ ಭೇಟಿಯನ್ನು ಬುಕ್ ಮಾಡಿ ಅಥವಾ ಉತ್ತರಗಳನ್ನು ಪಡೆಯಿರಿ — ನಿಮ್ಮಿಂದ ಕೇಳಲು ನಾವು ಇಷ್ಟಪಡುತ್ತೇವೆ.",
  "admission.visitUs": "ನಮ್ಮನ್ನು ಭೇಟಿ ಮಾಡಿ",
  "admission.schoolHours": "ಶಾಲಾ ಸಮಯ",
  "admission.callNow": "ಈಗ ಕರೆ ಮಾಡಿ",
  "admission.whatsapp": "ವಾಟ್ಸಾಪ್",
  "admission.whatsappSub": "ತ್ವರಿತ ಉತ್ತರ",

  // Inquiry Form
  "inquiry.parentName": "ಪೋಷಕರ ಹೆಸರು",
  "inquiry.parentNamePh": "ಉದಾ: ಪ್ರಿಯಾ ಶರ್ಮಾ",
  "inquiry.phone": "ಫೋನ್ / ವಾಟ್ಸಾಪ್",
  "inquiry.phonePh": "+91 98xxx xxxxx",
  "inquiry.email": "ಇಮೇಲ್",
  "inquiry.emailPh": "you@email.com",
  "inquiry.program": "ಆಸಕ್ತಿಯ ಕಾರ್ಯಕ್ರಮ",
  "inquiry.programPh": "ಕಾರ್ಯಕ್ರಮ ಆಯ್ಕೆಮಾಡಿ",
  "inquiry.childName": "ಮಗುವಿನ ಹೆಸರು",
  "inquiry.childNamePh": "ಪುಟ್ಟ ಮಗುವಿನ ಹೆಸರು",
  "inquiry.childAge": "ಮಗುವಿನ ವಯಸ್ಸು",
  "inquiry.childAgePh": "ಉದಾ: 3 ವರ್ಷ",
  "inquiry.message": "ಇನ್ನೇನಾದರೂ ಹಂಚಿಕೊಳ್ಳಬೇಕೆ?",
  "inquiry.messagePh": "ನಿಮ್ಮ ಮಗು ಅಥವಾ ಪ್ರಶ್ನೆಗಳ ಬಗ್ಗೆ ಹೇಳಿ…",
  "inquiry.send": "ವಿಚಾರಣೆ ಕಳುಹಿಸಿ",
  "inquiry.sending": "ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…",
  "inquiry.disclaimer": "ಸಲ್ಲಿಸಿದ ನಂತರ, ನಿಮ್ಮ ವಿವರಗಳೊಂದಿಗೆ ವಾಟ್ಸಾಪ್ ತೆರೆಯುತ್ತದೆ — ಕೇವಲ 'ಕಳುಹಿಸಿ' ಒತ್ತಿ, ತ್ವರಿತ ಉತ್ತರ ಪಡೆಯಿರಿ.",
  "inquiry.successTitle": "ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿದ್ದಕ್ಕೆ ಧನ್ಯವಾದಗಳು!",
  "inquiry.successBody": "ನಿಮ್ಮ ವಿಚಾರಣೆಯನ್ನು ನಾವು ಸ್ವೀಕರಿಸಿದ್ದೇವೆ. ವಾಟ್ಸಾಪ್‌ನಲ್ಲಿ ದೃಢೀಕರಿಸಲು ಕೆಳಗಿನ ಬಟನ್ ಒತ್ತಿ — ನಿಮಗೆ ತ್ವರಿತ ಉತ್ತರ ಸಿಗುತ್ತದೆ ಮತ್ತು ನಮ್ಮ ತಂಡ ಶಾಲಾ ಭೇಟಿಯನ್ನು ಗೊತ್ತುಪಡಿಸಲು ಒಂದು ಕೆಲಸದ ದಿನದೊಳಗೆ ನಿಮ್ಮನ್ನು ಕರೆ ಮಾಡುತ್ತದೆ.",
  "inquiry.confirmWa": "ವಾಟ್ಸಾಪ್‌ನಲ್ಲಿ ದೃಢೀಕರಿಸಿ",
  "inquiry.submitAnother": "ಇನ್ನೊಂದು ವಿಚಾರಣೆ ಸಲ್ಲಿಸಿ",
  "inquiry.errRequired": "ನಿಮ್ಮನ್ನು ತಲುಪಲು ದಯವಿಟ್ಟು ನಿಮ್ಮ ಹೆಸರು ಮತ್ತು ಫೋನ್ ಹಂಚಿಕೊಳ್ಳಿ.",
  "inquiry.errRate": "ಹೆಚ್ಚು ವಿನಂತಿಗಳು. ದಯವಿಟ್ಟು ನಂತರ ಪ್ರಯತ್ನಿಸಿ ಅಥವಾ ವಾಟ್ಸಾಪ್‌ನಲ್ಲಿ ಸಂದೇಶ ಕಳುಹಿಸಿ.",
  "inquiry.errGeneric": "ನಿಮ್ಮ ವಿಚಾರಣೆಯನ್ನು ಸಲ್ಲಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ವಾಟ್ಸಾಪ್ ಪ್ರಯತ್ನಿಸಿ.",
  "inquiry.thankToast": "ಧನ್ಯವಾದಗಳು! ವಾಟ್ಸಾಪ್ ತೆರೆಯಲಾಗುತ್ತಿದೆ…",
  "inquiry.fallbackToast": "ವಿಚಾರಣೆಯನ್ನು ಪೂರ್ಣಗೊಳಿಸಲು 'ವಾಟ್ಸಾಪ್‌ನಲ್ಲಿ ದೃಢೀಕರಿಸಿ' ಒತ್ತಿ.",

  // Footer
  "footer.tagline": "2 – 6 ವಯಸ್ಸಿನ ಪುಟ್ಟ ಕನಸುಗಾರರಿಗಾಗಿ ಸುರಕ್ಷಿತ, ಸಂತೋಷದ ಮತ್ತು ಚಟುವಟಿಕೆ-ಆಧಾರಿತ ಕಲಿಕೆಯ ಮನೆ. ದೊಡ್ಡ ಕನಸುಗಳು ಇಲ್ಲಿ ಆರಂಭವಾಗುತ್ತವೆ.",
  "footer.visitUs": "ನಮ್ಮನ್ನು ಭೇಟಿ ಮಾಡಿ",
  "footer.admissions": "ಪ್ರವೇಶ",
  "footer.rights": "ಎಲ್ಲಾ ಹಕ್ಕುಗಳನ್ನು ಕಾಯ್ದಿರಿಸಲಾಗಿದೆ.",
  "footer.adminLogin": "ಅಡ್ಮಿನ್ ಲಾಗಿನ್",
  "footer.madeWith": "ಪ್ರೀತಿಯಿಂದ ಮಾಡಿದೆ",
  "footer.forDreamers": "ಪುಟ್ಟ ಕನಸುಗಾರರಿಗಾಗಿ",

  // WhatsApp Float
  "wa.float": "ನಮ್ಮೊಂದಿಗೆ ಚಾಟ್ ಮಾಡಿ",

  // Language
  "lang.label": "ಭಾಷೆ",
  "lang.en": "English",
  "lang.kn": "ಕನ್ನಡ",

  // Admission Form (public)
  "af.title": "ವಿದ್ಯಾರ್ಥಿ ಪ್ರವೇಶ ಅರ್ಜಿ",
  "af.subtitle": "ಆಡಿ · ಕಲಿಯಿರಿ · ಬೆಳೆಯಿರಿ",
  "af.subtitle2": "ಎನ್‌ಇಪಿ 2020 ಪ್ರೇರಿತ ಪಠ್ಯಕ್ರಮ",
  "af.step.student": "ವಿದ್ಯಾರ್ಥಿ",
  "af.step.address": "ವಿಳಾಸ",
  "af.step.parents": "ಪೋಷಕರು",
  "af.step.medical": "ವೈದ್ಯಕೀಯ & ಶಾಲೆ",
  "af.step.pickup": "ಪಿಕಪ್ & ದಾಖಲೆ",
  "af.section.seeking": "1. ಯಾವ ವರ್ಗಕ್ಕೆ ಪ್ರವೇಶ",
  "af.section.student": "2. ವಿದ್ಯಾರ್ಥಿ ಮಾಹಿತಿ",
  "af.section.address": "3. ವಿಳಾಸ",
  "af.section.father": "4. ತಂದೆಯ ವಿವರಗಳು",
  "af.section.mother": "5. ತಾಯಿಯ ವಿವರಗಳು",
  "af.section.medical": "6. ವೈದ್ಯಕೀಯ ಮಾಹಿತಿ",
  "af.section.prevSchool": "7. ಹಿಂದಿನ ಶಾಲೆ",
  "af.section.pickup": "8. ಪಿಕ್-ಅಪ್ ಮತ್ತು ಡ್ರಾಪ್",
  "af.section.emergency": "9. ತುರ್ತು ಸಂಪರ್ಕ",
  "af.section.docs": "10. ಸಲ್ಲಿಸಿದ ದಾಖಲೆಗಳು",
  "af.section.declaration": "11. ಪೋಷಕರ ಘೋಷಣೆ",
  "af.field.firstName": "ಮೊದಲ ಹೆಸರು",
  "af.field.middleName": "ಮಧ್ಯದ ಹೆಸರು",
  "af.field.lastName": "ಕೊನೆಯ ಹೆಸರು",
  "af.field.dob": "ಜನನ ದಿನಾಂಕ",
  "af.field.gender": "ಲಿಂಗ",
  "af.field.male": "ಗಂಡು",
  "af.field.female": "ಹೆಣ್ಣು",
  "af.field.bloodGroup": "ರಕ್ತದ ಗುಂಪು",
  "af.field.bloodGroupPh": "ಉದಾ: O+",
  "af.field.aadhaar": "ಆಧಾರ್ ಸಂಖ್ಯೆ (ಐಚ್ಛಿಕ)",
  "af.field.nationality": "ರಾಷ್ಟ್ರೀಯತೆ",
  "af.field.motherTongue": "ಮಾತೃಭಾಷೆ",
  "af.field.religion": "ಧರ್ಮ",
  "af.field.otherLangs": "ಇತರ ಭಾಷೆಗಳು",
  "af.field.category": "ವರ್ಗ (ಐಚ್ಛಿಕ)",
  "af.field.photo": "ವಿದ್ಯಾರ್ಥಿ ಪಾಸ್‌ಪೋರ್ಟ್ ಫೋಟೋ (ಐಚ್ಛಿಕ, 35×45mm)",
  "af.field.house": "ಮನೆ ಸಂಖ್ಯೆ",
  "af.field.street": "ಬೀದಿ",
  "af.field.area": "ಪ್ರದೇಶ",
  "af.field.landmark": "ಗುರುತು",
  "af.field.city": "ಗ್ರಾಮ / ನಗರ",
  "af.field.taluk": "ತಾಲ್ಲೂಕು",
  "af.field.district": "ಜಿಲ್ಲೆ",
  "af.field.state": "ರಾಜ್ಯ",
  "af.field.pin": "ಪಿನ್ ಕೋಡ್",
  "af.field.fullName": "ಪೂರ್ಣ ಹೆಸರು",
  "af.field.qualification": "ವಿದ್ಯಾರ್ಹತೆ",
  "af.field.occupation": "ವೃತ್ತಿ",
  "af.field.company": "ಕಂಪನಿ ಹೆಸರು",
  "af.field.mobile": "ಮೊಬೈಲ್ ಸಂಖ್ಯೆ",
  "af.field.whatsapp": "ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆ",
  "af.field.emailId": "ಇಮೇಲ್ ಐಡಿ",
  "af.field.specialNeeds": "ವಿಶೇಷ ಅಗತ್ಯಗಳು",
  "af.field.allergies": "ಅಲರ್ಜಿಗಳು",
  "af.field.medConditions": "ವೈದ್ಯಕೀಯ ಸ್ಥಿತಿಗಳು",
  "af.field.medication": "ಪ್ರಸ್ತುತ ಔಷಧಿ",
  "af.field.doctor": "ವೈದ್ಯರ ಹೆಸರು",
  "af.field.hospital": "ಆಸ್ಪತ್ರೆ",
  "af.field.emergencyNumber": "ತುರ್ತು ಸಂಪರ್ಕ ಸಂಖ್ಯೆ",
  "af.field.prevSchool": "ಶಾಲೆಯ ಹೆಸರು",
  "af.field.prevClass": "ಓದಿದ ತರಗತಿ",
  "af.field.prevReason": "ಬಿಡುವ ಕಾರಣ",
  "af.field.pickupOther": "ಇತರ ಪಿಕಪ್ ಪ್ರಕಾರವನ್ನು ನಿರ್ದಿಷ್ಟಪಡಿಸಿ",
  "af.field.authorizedName": "ಅಧಿಕೃತ ವ್ಯಕ್ತಿಯ ಹೆಸರು",
  "af.field.relationship": "ಸಂಬಂಧ",
  "af.field.phoneNumber": "ಫೋನ್ ಸಂಖ್ಯೆ",
  "af.field.addressIfDiff": "ವಿಳಾಸ (ವಿಭಿನ್ನವಾಗಿದ್ದರೆ)",
  "af.field.name": "ಹೆಸರು",
  "af.field.altPhone": "ಪರ್ಯಾಯ ಫೋನ್",
  "af.pickup.parent": "ಪೋಷಕರು",
  "af.pickup.grandparent": "ಅಜ್ಜಿ/ತಾತ",
  "af.pickup.driver": "ಚಾಲಕ",
  "af.pickup.van": "ವ್ಯಾನ್ ಸೇವೆ",
  "af.pickup.other": "ಇತರೆ",
  "af.doc.birth": "ಜನನ ಪ್ರಮಾಣಪತ್ರ",
  "af.doc.aadhaar": "ಆಧಾರ್ ನಕಲು",
  "af.doc.photos": "ಪಾಸ್‌ಪೋರ್ಟ್ ಫೋಟೋಗಳು",
  "af.doc.vaccine": "ವ್ಯಾಕ್ಸಿನೇಷನ್ ದಾಖಲೆ",
  "af.doc.address": "ವಿಳಾಸದ ಪುರಾವೆ",
  "af.doc.parentId": "ಪೋಷಕರ ಗುರುತಿನ ಪುರಾವೆ",
  "af.doc.tc": "ವರ್ಗಾವಣೆ ಪ್ರಮಾಣಪತ್ರ (ಅನ್ವಯಿಸಿದರೆ)",
  "af.declarationText": "ನೀಡಿದ ಮಾಹಿತಿಯು ಸತ್ಯ ಮತ್ತು ಸರಿಯಾಗಿದೆ ಎಂದು ನಾನು ಘೋಷಿಸುತ್ತೇನೆ. ಲಿಟಲ್ ಆರ್ಕ್ ಪ್ರೀಸ್ಕೂಲ್‌ನ ನಿಯಮಗಳು ಮತ್ತು ನಿಬಂಧನೆಗಳಿಗೆ ಬದ್ಧನಾಗಲು ನಾನು ಒಪ್ಪಿಕೊಳ್ಳುತ್ತೇನೆ. ಪ್ರವೇಶದ ಸಮಯದಲ್ಲಿ ದೈಹಿಕ ಸಹಿಯನ್ನು ಸಂಗ್ರಹಿಸಲಾಗುತ್ತದೆ ಎಂದು ನಾನು ಅರ್ಥಮಾಡಿಕೊಂಡಿದ್ದೇನೆ.",
  "af.back": "ಹಿಂದೆ",
  "af.continue": "ಮುಂದುವರಿಸಿ",
  "af.submit": "ಪ್ರವೇಶ ಅರ್ಜಿ ಸಲ್ಲಿಸಿ",
  "af.submitting": "ಸಲ್ಲಿಸಲಾಗುತ್ತಿದೆ…",
  "af.errRequired": "ದಯವಿಟ್ಟು ಅಗತ್ಯ ಕ್ಷೇತ್ರಗಳನ್ನು ಭರ್ತಿ ಮಾಡಿ.",
  "af.errDeclaration": "ದಯವಿಟ್ಟು ಪೋಷಕರ ಘೋಷಣೆಗೆ ಒಪ್ಪಿಗೆ ನೀಡಿ.",
  "af.errGeneric": "ಸಲ್ಲಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.",
  "af.invalid.title": "ಲಿಂಕ್ ಲಭ್ಯವಿಲ್ಲ",
  "af.invalid.help": "ಹೊಸ ಪ್ರವೇಶ ಲಿಂಕ್‌ಗಾಗಿ ದಯವಿಟ್ಟು ಲಿಟಲ್ ಆರ್ಕ್‌ನ ಸಮನ್ವಯಕರನ್ನು ಸಂಪರ್ಕಿಸಿ.",
  "af.invalid.home": "ಲಿಟಲ್ ಆರ್ಕ್‌ಗೆ ಭೇಟಿ ನೀಡಿ",
  "af.done.title": "ಪ್ರವೇಶ ಅರ್ಜಿ ಸಲ್ಲಿಸಲಾಗಿದೆ!",
  "af.done.body": "{name} ಗಾಗಿ ಲಿಟಲ್ ಆರ್ಕ್ ಪ್ರೀಸ್ಕೂಲ್ ಅನ್ನು ಆಯ್ಕೆ ಮಾಡಿದ್ದಕ್ಕೆ ಧನ್ಯವಾದಗಳು. ಪ್ರಕ್ರಿಯೆಯನ್ನು ಪೂರ್ಣಗೊಳಿಸಲು ನಮ್ಮ ಸಮನ್ವಯಕರು ಶೀಘ್ರದಲ್ಲೇ ನಿಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸುತ್ತಾರೆ.",
  "af.done.home": "ಮನೆಗೆ ಭೇಟಿ ನೀಡಿ",
};

const DICTS = { en, kn };

// ===== Provider =====
const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage?.getItem(STORAGE_KEY);
    return stored === "kn" ? "kn" : "en";
  });

  const setLang = useCallback((next) => {
    if (next !== "en" && next !== "kn") return;
    setLangState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    document.documentElement.setAttribute("lang", next);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const t = useCallback(
    (key, params) => {
      const dict = DICTS[lang] || DICTS.en;
      let raw = dict[key];
      if (raw == null) {
        raw = DICTS.en[key];
      }
      if (raw == null) return key;
      if (params && typeof raw === "string") {
        return raw.replace(/\{(\w+)\}/g, (_, k) => (params[k] != null ? params[k] : ""));
      }
      return raw;
    },
    [lang]
  );

  const value = useMemo(() => ({ t, lang, setLang }), [t, lang, setLang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useT = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Fallback for components rendered outside the provider (e.g. isolated tests).
    return {
      t: (key) => DICTS.en[key] ?? key,
      lang: "en",
      setLang: () => {},
    };
  }
  return ctx;
};
